import { forwardRef, useCallback } from "react";
import { SnackbarProvider as NotistackProvider, useSnackbar } from "notistack";
import { Alert, styled } from "@mzc-pdc/ui";
import { keyframes, alpha } from "@mui/material";
import { IconCompleteCircle, IconErrorCircle, IconWarningTriangle, IconInfoCircle } from "@mzc-cloudplex/icons";

const AUTO_HIDE_DURATION = 3000;

// PDC-UI Snackbar의 Timer 애니메이션 재현
const timerAnimation = keyframes`
    from {
        width: 100%;
    }
    to {
        width: 0;
    }
`;

// progressbar가 포함된 Alert 래퍼
const StyledAlertWrapper = styled("div", {
    shouldForwardProp: (prop) => prop !== "duration",
})(({ theme, duration }) => ({
    position: "relative",
    "&:after": {
        content: "''",
        position: "absolute",
        left: 0,
        bottom: 0,
        width: 0,
        height: 6,
        animation: `${timerAnimation} ${duration}ms linear`,
        animationPlayState: "running",
        backgroundColor: alpha(theme.palette.constant.white, 0.6),
        borderRadius: "0 0 0 4px",
    },
    "&:hover": {
        "&:after": {
            animationPlayState: "paused",
        },
    },
}));

// variant에 따른 아이콘 매핑
const VARIANT_ICON_MAP = {
    success: <IconCompleteCircle size={20} />,
    error: <IconErrorCircle size={20} />,
    warning: <IconWarningTriangle size={20} />,
    info: <IconInfoCircle size={20} />,
    default: <IconInfoCircle size={20} />,
};

let snackbarRef = null;

// 컴포넌트 외부에서도 custom hook을 사용할 수 있도록 작업
export const SnackbarUtilsConfigurator = () => {
    snackbarRef = useSnackbar();
    return null;
};

export const snackbarUtils = {
    success(message, options) {
        snackbarRef?.enqueueSnackbar(message, { variant: "success", ...options });
    },
    error(message, options) {
        snackbarRef?.enqueueSnackbar(message, { variant: "error", ...options });
    },
    warning(message, options) {
        snackbarRef?.enqueueSnackbar(message, { variant: "warning", ...options });
    },
    info(message, options) {
        snackbarRef?.enqueueSnackbar(message, { variant: "info", ...options });
    },
    default(message, options) {
        snackbarRef?.enqueueSnackbar(message, { variant: "default", ...options });
    },
    close(key) {
        snackbarRef?.closeSnackbar(key);
    },
};

const SnackbarContent = forwardRef(({ id, message, variant = "default", ...rest }, ref) => {
    const { closeSnackbar } = useSnackbar();

    const handleClose = useCallback(() => {
        closeSnackbar(id);
    }, [closeSnackbar, id]);

    const severity = variant === "default" ? "info" : variant;
    const color = variant === "default" ? undefined : variant;
    const icon = VARIANT_ICON_MAP[variant] ?? VARIANT_ICON_MAP.default;

    return (
        <StyledAlertWrapper ref={ref} duration={AUTO_HIDE_DURATION}>
            <Alert severity={severity} color={color} variant="filled" onClose={handleClose} icon={icon} {...rest}>
                {message}
            </Alert>
        </StyledAlertWrapper>
    );
});

SnackbarContent.displayName = "SnackbarContent";

const SnackbarProvider = ({ children }) => {
    return (
        <NotistackProvider
            maxSnack={10}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            autoHideDuration={AUTO_HIDE_DURATION}
            Components={{
                success: SnackbarContent,
                error: SnackbarContent,
                warning: SnackbarContent,
                info: SnackbarContent,
                default: SnackbarContent,
            }}
        >
            <SnackbarUtilsConfigurator />
            {children}
        </NotistackProvider>
    );
};

export default SnackbarProvider;
